import { useState } from 'react';
import { format, parse } from 'date-fns';

import { ROUND_TRIP_JOURNEY_TYPE } from '~constants/pointToPointTicket';

import type { RangeDate } from '@myrealtrip/web-ui';
import type { RoundTripJourneyType } from '~models/Transportation/PointToPointTicket';

interface Props {
  isSingleTrip: boolean;
  departureDate: string | null;
  arrivalDate: string | null | undefined;
  outboundDepartureTime?: string | null;
  inboundDepartureTime?: string | null;
  onApply: (params: {
    startDate: string;
    outboundStartTime?: string;
    endDate?: string;
    inboundStartTime?: string;
  }) => void;
}

const useDateSearch = ({
  isSingleTrip,
  departureDate,
  arrivalDate,
  outboundDepartureTime,
  inboundDepartureTime,
  onApply,
}: Props) => {
  const [startDate, setStartDate] = useState<Date | undefined>(
    departureDate ? parse(departureDate, 'yyyy-MM-dd', new Date()) : undefined,
  );
  const [endDate, setEndDate] = useState<Date | undefined>(
    !isSingleTrip && arrivalDate ? parse(arrivalDate, 'yyyy-MM-dd', new Date()) : undefined,
  );

  const [outboundStartTime, setOutboundStartTime] = useState<string | null | undefined>(
    outboundDepartureTime,
  );
  const [inboundStartTime, setInboundStartTime] = useState<string | null | undefined>(
    !isSingleTrip && inboundDepartureTime ? inboundDepartureTime : undefined,
  );

  const handleChangeSingleTripDate = (date: Date) => {
    setStartDate(date);
  };

  const handleChangeRoundTripDates = (rangeDate: RangeDate) => {
    const [selectedStartDate, selectedEndDate] = rangeDate;

    setStartDate(selectedStartDate);
    setEndDate(selectedEndDate);
  };

  const handleChangeDepartureTime = (type: RoundTripJourneyType, time: string) => {
    if (type === ROUND_TRIP_JOURNEY_TYPE.OUTBOUND) {
      setOutboundStartTime(time);
    } else {
      setInboundStartTime(time);
    }
  };

  const handleApply = () => {
    if (isSingleTrip && startDate) {
      onApply({
        startDate: format(startDate, 'yyyy-MM-dd'),
        ...(outboundStartTime && { outboundStartTime }),
      });
    } else if (!isSingleTrip && startDate && endDate) {
      onApply({
        startDate: format(startDate, 'yyyy-MM-dd'),
        endDate: format(endDate, 'yyyy-MM-dd'),
        ...(outboundStartTime && { outboundStartTime }),
        ...(inboundStartTime && { inboundStartTime }),
      });
    }
  };

  const handleReset = () => {
    setStartDate(undefined);
    setEndDate(undefined);
    setOutboundStartTime(undefined);
    setInboundStartTime(undefined);
  };

  return {
    startDate,
    endDate,
    outboundStartTime,
    inboundStartTime,
    handleChangeSingleTripDate,
    handleChangeRoundTripDates,
    handleChangeDepartureTime,
    handleApply,
    handleReset,
  };
};

export default useDateSearch;
